import { CategorySlug, TOP_BAR_CATEGORY_GROUPS } from '../config';
import { useInitialState } from '../contexts/InitialStateContext';
import { useTranslation } from '@wix/wix-i18n-config';
import { useInternalRoutes } from './useInternalRoutes';
import { useSelectedCategory } from './useSelectedCategory';
import { useMemo } from 'react';
import { useIsStudio2Ui } from './useIsStudio2Ui';

export interface MenuCategory {
  url: string;
  isActive: boolean;
  label: string;
  slug: string;
}

export interface MenuCategoryGroup {
  title: string;
  categories: MenuCategory[];
}

const STUDIO2_HIDDEN_GROUPS = [
  'app.topBar.menu.buckets.features',
  'app.topBar.menu.buckets.style',
];

export const useMenuCategoryGroups = (): MenuCategoryGroup[] => {
  const { book } = useInitialState();
  const { t } = useTranslation();
  const { category: categoryRoute } = useInternalRoutes();
  const selectedCategory = useSelectedCategory();
  const isStudio2Ui = useIsStudio2Ui();

  return useMemo(
    () =>
      TOP_BAR_CATEGORY_GROUPS.filter(
        ({ title }) => !(isStudio2Ui && STUDIO2_HIDDEN_GROUPS.includes(title)),
      ).map(({ categorySlugs, title }) => {
        return {
          title: t(title),
          categories: categorySlugs
            // inspirational templates are Studio 1 only
            .filter(
              (slug) => !(isStudio2Ui && slug === CategorySlug.INSPIRATIONAL),
            )
            .map((slug) => {
              const category = book?.categories.find((c) => c.slug === slug);

              return category
                ? {
                    url: categoryRoute.build(slug),
                    isActive: slug === selectedCategory?.slug,
                    label: category.displayName,
                    slug,
                  }
                : false;
            })
            .filter(Boolean) as MenuCategory[],
        };
      }),
    [book?.categories, categoryRoute, selectedCategory?.slug, t, isStudio2Ui],
  );
};
